// apps/web/src/pages/ImageFilter.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import {
  FILTER_PRESETS,
  type FilterPresetId,
  getWebFilterStyle,
} from '../../../shared/components/filterHelpers.web';
import { useTranslation } from 'react-i18next';

type LocState = {
  sidebarData?: any;
  mediaItem?: { id: string; file: File; filterId?: FilterPresetId };
};

const SIGN: Record<FilterPresetId, string> = {
  none:   '◎',

  // subtle
  soft:   '▫️',
  warm:   '🔆',
  icy:    '❄️',
  pastel: '🎨',

  // medium
  sunny:  '☀️',
  frozen: '🧊',
  dusk:   '🌇',
  noir:   '⬛',
  gold:   '🥇',
  rose:   '🌹',
  teal:   '🌊',
  amber:  '🟧',
  film:   '🎞️',

  // strongest
  cinema: '🎬',
};

export default function ImageFilter() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation() as { state?: LocState };
  const { mediaItems, updateMediaItem } = useMediaPreview();
  const { t } = useTranslation();

  const mediaItem = state?.mediaItem || mediaItems.find(m => m.id === id);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Use existing preset on the item if present, otherwise 'none'
  const initialPreset = (mediaItem as any)?.filterId as FilterPresetId | undefined;
  const [filterId, setFilterId] = useState<FilterPresetId>(initialPreset ?? 'none');

  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!mediaItem?.file) return;
    const url = URL.createObjectURL(mediaItem.file);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [mediaItem?.file]);

  // Current preset styles (live preview)
  const { cssFilter, overlay } = useMemo(
    () => getWebFilterStyle(filterId),
    [filterId]
  );

  // Save: bake ONLY the preset (cssFilter + overlay) into a new image
  const handleSave = async () => {
    if (!imageUrl || !mediaItem) return;
    const src = await loadImage(imageUrl);

    const canvas = document.createElement('canvas');
    canvas.width = src.naturalWidth;
    canvas.height = src.naturalHeight;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // draw with css-like filter
    ctx.filter = cssFilter ?? 'none';
    ctx.drawImage(src, 0, 0);

    // tint overlay (multiply for broad browser support; close to mobile feel)
    if (overlay?.tint && (overlay.opacity ?? 0) > 0) {
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = overlay.opacity!;
      ctx.fillStyle = overlay.tint!;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }

    const blob: Blob = await new Promise((res) =>
      canvas.toBlob(b => res(b!), 'image/jpeg', 0.92)
    );
    const file = new File([blob], `edited-${Date.now()}.jpg`, { type: 'image/jpeg' });

    // Persist: keep the chosen filterId on the item, replace file
    updateMediaItem({ ...mediaItem, file, filterId } as any);

    const targetId = mediaItem.id;

    navigate('/gallery-posting', {
      state: {
        sidebarData: state?.sidebarData,
        previewFile: file,
        previewFilename: file.name,
        editTargetId: targetId,
        isPreview: true,
      },
    });
  };

  const resetPreset = () => setFilterId('none');

 return (
  <div style={pageShellMobile}>
    {/* IMAGE PREVIEW TOP */}
    <div style={previewCardMobile}>
      {!mediaItem?.file ? (
        <div style={previewMessage}>
          {t('imageFilter.messages.noMedia')}
        </div>
      ) : !imageUrl ? (
        <div style={previewMessage}>
          {t('imageFilter.messages.loading')}
        </div>
      ) : (
        <div style={previewInnerMobile}>
          <img
            src={imageUrl}
            alt=""
            ref={imgRef}
            style={{
              ...previewImageMobile,
              filter: cssFilter ?? 'none',
            }}
          />

          {!!overlay?.tint && (overlay.opacity ?? 0) > 0 && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: overlay.tint,
                opacity: overlay.opacity,
                mixBlendMode:
                  filterId === 'noir' ? 'multiply' : 'overlay',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      )}
    </div>

    {/* HORIZONTAL FILTER SCROLLER */}
    <div style={filterScrollRow}>
      {FILTER_PRESETS.map((p) => {
        const active = filterId === p.id;
        const { overlay: sw, cssFilter: previewFilter } =
          getWebFilterStyle(p.id);

        return (
          <button
            key={p.id}
            onClick={() => setFilterId(p.id)}
            style={{
              ...filterTileMobile,
              ...(active ? filterTileActive : null),
            }}
            title={p.hint}
          >
            <div style={filterIconBubble}>
              <span style={{ fontSize: 18 }}>{SIGN[p.id]}</span>
            </div>

            <div style={filterTextCol}>
              <span style={filterName}>{p.label}</span>
            </div>

            <div style={filterPreviewChip}>
              <div
                style={{
                  ...filterPreviewChipInner,
                  filter: previewFilter ?? 'none',
                }}
              >
                {!!sw?.tint && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: sw.tint,
                      opacity: sw.opacity ?? 0,
                      mixBlendMode:
                        p.id === 'noir' ? 'multiply' : 'overlay',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </div>
            </div>
          </button>
        );
      })}
    </div>

    {/* ACTION BUTTONS */}
    <div style={mobileActions}>
      <button onClick={resetPreset} style={secondaryBtn}>
        🔄 {t('imageFilter.buttons.reset')}
      </button>

      <button
        onClick={() =>
          navigate('/gallery-posting', {
            replace: true,
            state: {
              sidebarData: state?.sidebarData ?? undefined,
              fromEditor: true,
            },
          })
        }
        style={secondaryBtn}
      >
        🔙 {t('imageFilter.buttons.back')}
      </button>

      <button onClick={handleSave} style={primaryBtn}>
        📎 {t('imageFilter.buttons.save')}
      </button>
    </div>
  </div>
);

}

/* ---------- helpers ---------- */

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = url;
  });
}

/* ---------- styles (aligned & single-page) ---------- */


/* Buttons – similar feel to GalleryPosting small actions */
const baseBtn: React.CSSProperties = {
  padding: '7px 8px',
  fontSize: 12,
  fontWeight: 500,
  borderRadius: 999,
  border: '1px solid #cbd5f5',
  cursor: 'pointer',
  width: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  transition: 'background-color 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease',
};

const secondaryBtn: React.CSSProperties = {
  ...baseBtn,
  backgroundColor: '#020617',
  color: '#cbd5f5',
};

const primaryBtn: React.CSSProperties = {
  ...baseBtn,
  background: 'linear-gradient(135deg, #facc15, #fbbf24)',
  color: '#111827',
  borderColor: 'rgba(250,204,21,0.9)',
  boxShadow: '0 8px 18px rgba(250,204,21,0.35)',
};

/* Filter tiles */


const filterTileActive: React.CSSProperties = {
  borderColor: '#ffd76a',
  background:
    'linear-gradient(145deg, rgba(250, 204, 21, 0.12), rgba(15, 23, 42, 0.96))',
};

const filterIconBubble: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 999,
  background: 'radial-gradient(circle at 30% 10%, #1f2937, #020617)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const filterTextCol: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const filterName: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: '#e5e7eb',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};


const filterPreviewChip: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 12,
  border: '1px solid rgba(148,163,184,0.6)',
  padding: 2,
  flexShrink: 0,
  boxSizing: 'border-box',
};

const filterPreviewChipInner: React.CSSProperties = {
  width: '100%',
  height: '100%',
  borderRadius: 10,
  background:
    'linear-gradient(135deg, #9ca3af, #020617)',
  position: 'relative',
  overflow: 'hidden',
};


/**
 * Make the image a little smaller so everything fits comfortably on one page.
 */

const previewMessage: React.CSSProperties = {
  fontSize: 14,
  color: '#9ca3af',
};

const pageShellMobile: React.CSSProperties = {
  height: '100vh',
  overflow: 'hidden',
  background: '#000',
  color: '#f9fafb',
  display: 'flex',
  flexDirection: 'column',
  padding: 10,
};

const previewCardMobile: React.CSSProperties = {
  borderRadius: 18,
  border: 'none',
  background: 'transparent',
  overflow: 'hidden',
  height: '76vh',   // same as video editor
};

const previewInnerMobile: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

const previewImageMobile: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  display: 'block',
};

const filterScrollRow: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  overflowX: 'auto',
  marginTop: 0,
  paddingBottom: 4,
};

const filterTileMobile: React.CSSProperties = {
  minWidth: 110,
  height: 42,
  borderRadius: 12,
  border: '1px solid rgba(148,163,184,0.35)',
  background:
    'radial-gradient(circle at top left, rgba(15,23,42,0.9), rgba(15,23,42,1))',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 8px',
  flexShrink: 0,
};

const mobileActions: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: 8,
  marginTop: 28,
};

